import { useCallback, useEffect, useState } from "react";
import { Ban, Search, RefreshCw, Unlock, History, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("ru-RU");
};

export function AdminBlockedUsers() {
  const [blocked, setBlocked] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [telegramId, setTelegramId] = useState("");
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadBlocked = useCallback(async () => {
    try {
      setBlocked(await api.adminBlockedUsers());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Не удалось загрузить блокировки");
    }
  }, []);

  useEffect(() => { loadBlocked(); }, [loadBlocked]);

  const search = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return setResults([]);
    setBusy(true);
    try {
      setResults(await api.adminSearchUsers(query.trim()));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось найти пользователей");
    } finally {
      setBusy(false);
    }
  };

  const showHistory = async (id) => {
    setSelected(Number(id));
    try {
      setHistory(await api.adminBlockHistory(id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Не удалось загрузить историю");
    }
  };

  const block = async (e) => {
    e.preventDefault();
    const id = Number(telegramId);
    if (!Number.isSafeInteger(id) || id <= 0) {
      toast.error("Укажите числовой Telegram ID");
      return;
    }
    setBusy(true);
    try {
      const person = results.find((item) => item.telegram_id === id) || {};
      const response = await api.adminBlockUser({
        telegram_id: id,
        username: person.username || "",
        name: person.name || "",
        reason,
      });
      const active = response.active_requests || [];
      toast.success(active.length
        ? `Пользователь заблокирован. Активных заявок: ${active.length}; они не отменены.`
        : "Пользователь заблокирован");
      setReason("");
      await Promise.all([loadBlocked(), showHistory(id)]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось заблокировать пользователя");
    } finally {
      setBusy(false);
    }
  };

  const unblock = async (person) => {
    const note = window.prompt("Причина разблокировки (необязательно):", "");
    if (note === null) return;
    setBusy(true);
    try {
      const response = await api.adminUnblockUser(person.telegram_id, note);
      toast.success(response.already_unblocked ? "Пользователь уже разблокирован" : "Пользователь разблокирован");
      await Promise.all([loadBlocked(), showHistory(person.telegram_id)]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось разблокировать пользователя");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-blocked-users">
      <section className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <h2 className="font-semibold flex items-center gap-2"><Ban size={18} /> Найти пользователя</h2>
        <form onSubmit={search} className="flex gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Telegram ID, @username или имя"
            data-testid="blocked-user-search"
            className="min-w-0 flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-blue-500/50 text-sm"
          />
          <button disabled={busy} className="px-3 rounded-xl bg-blue-500/20 text-blue-200" aria-label="Найти">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
          </button>
        </form>

        {results.length > 0 && (
          <div className="space-y-2">
            {results.map((person) => (
              <div key={person.telegram_id} className="p-3 rounded-xl bg-white/5 border border-white/10">
                <button
                  type="button"
                  data-testid={`blocked-user-result-${person.telegram_id}`}
                  onClick={() => setTelegramId(String(person.telegram_id))}
                  className="w-full text-left"
                >
                  <div className="font-medium">{person.name || "Без имени"} {person.username && <span className="text-blue-300">@{person.username}</span>}</div>
                  <div className="text-xs text-slate-400 mt-1">Telegram ID: {person.telegram_id} · найден в: {person.sources.join(", ")}</div>
                  {person.blocked && <div className="text-xs text-rose-300 mt-1">Уже заблокирован</div>}
                  {!!person.active_requests?.length && (
                    <div className="text-xs text-amber-300 mt-1" data-testid={`blocked-user-active-${person.telegram_id}`}>
                      Активных заявок: {person.active_requests.length} · блокировка их не отменит
                    </div>
                  )}
                </button>
                <button type="button" onClick={() => showHistory(person.telegram_id)} className="mt-2 text-xs text-blue-300 flex items-center gap-1">
                  <History size={14} /> История блокировок
                </button>
              </div>
            ))}
          </div>
        )}
        {query.trim() && !busy && results.length === 0 && (
          <p className="text-xs text-slate-400">Совпадений нет. Можно ввести Telegram ID вручную.</p>
        )}
      </section>

      <form onSubmit={block} className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <h2 className="font-semibold">Заблокировать получение аккаунтов</h2>
        <label className="block text-xs text-slate-400">
          Числовой Telegram ID
          <input
            type="number" min="1" step="1" required value={telegramId}
            onChange={(e) => setTelegramId(e.target.value)}
            data-testid="blocked-user-id"
            className="mt-1 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white"
          />
        </label>
        <label className="block text-xs text-slate-400">
          Причина
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)} minLength={1} maxLength={500} required rows={2}
            data-testid="blocked-user-reason"
            className="mt-1 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white"
          />
        </label>
        <button disabled={busy} data-testid="blocked-user-submit" className="w-full py-2.5 rounded-xl bg-rose-500/20 text-rose-200 font-semibold">
          Заблокировать
        </button>
      </form>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Заблокированные пользователи</h2>
          <button onClick={loadBlocked} className="p-2 rounded-lg bg-white/5" aria-label="Обновить список"><RefreshCw size={16} /></button>
        </div>
        {blocked.length === 0 && <p className="text-sm text-slate-500">Список пуст</p>}
        {blocked.map((person) => (
          <article key={person.telegram_id} data-testid={`blocked-user-${person.telegram_id}`} className="rounded-2xl bg-white/[0.03] border border-white/10 p-3">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{person.name || "Без имени"} {person.username && <span className="text-blue-300">@{person.username}</span>}</div>
                <div className="text-xs text-slate-400 mt-1">Telegram ID: {person.telegram_id}</div>
                <div className="text-xs text-slate-400">Заблокировал: {person.blocked_by || "—"} · {fmtDate(person.blocked_at)}</div>
                {person.reason && <div className="text-sm text-slate-300 mt-2">Причина: {person.reason}</div>}
                {!!person.active_requests?.length && (
                  <div className="mt-2 p-2 rounded-lg bg-amber-500/10 text-xs text-amber-200" data-testid={`blocked-active-requests-${person.telegram_id}`}>
                    Активные заявки ({person.active_requests.length}) сохранены: {person.active_requests.map((r) => `${r.id} · ${r.status}`).join(", ")}
                  </div>
                )}
              </div>
              <button disabled={busy} onClick={() => unblock(person)} className="shrink-0 flex items-center gap-1 px-3 py-2 rounded-lg bg-emerald-500/15 text-emerald-200 text-xs">
                <Unlock size={14} /> Разблокировать
              </button>
            </div>
            <button onClick={() => showHistory(person.telegram_id)} className="mt-3 text-xs text-blue-300 flex items-center gap-1">
              <History size={14} /> История
            </button>
          </article>
        ))}
      </section>

      {selected && (
        <section className="rounded-2xl bg-white/[0.03] border border-white/10 p-4" data-testid="blocked-user-history">
          <h2 className="font-semibold mb-3">История · Telegram ID {selected}</h2>
          {history.length === 0 ? <p className="text-sm text-slate-500">Записей пока нет</p> : (
            <div className="space-y-2">
              {history.map((event) => (
                <div key={event.id} className="text-xs border-l-2 border-blue-400/50 pl-3 py-1">
                  <div className="font-medium">{event.action === "blocked" ? "Блокировка" : "Разблокировка"} · {fmtDate(event.at)}</div>
                  <div className="text-slate-400">Администратор: {event.admin || "—"}</div>
                  {event.reason && <div className="text-slate-400">Причина: {event.reason}</div>}
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
